import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Modal from '@/components/modal/Modal';
import { Col, Row, getItemWithExpiration } from '@/lib/utils';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import { CodeValueType } from '@/types/types';
import dayjs from 'dayjs';
import SvgIcon from '@/components/SvgIcon';
import { useCoachWorkRoutines, usePatchCoachWorkRoutines } from '@/features/coachWorkRoutine';
import { useCoachWorkExceptions, usePatchCoachWorkExceptions } from '@/features/coachWorkException';
import { WeeklyRoutineItem, CoachWorkRoutine, CoachWorkRoutineBody } from '@/features/coachWorkRoutine/types';
import { WorkException, CoachWorkExceptionBody } from '@/features/coachWorkException/types';
import { colors } from '@/lib/colors';
import { overlay } from 'overlay-kit';

import { WeekSchedule, ExceptionInclude, ExceptionExclude, DAYS } from './types/workingHours';
import { DefaultScheduleTab } from './components/DefaultScheduleTab';
import { ExceptionScheduleTab } from './components/ExceptionScheduleTab';
import { UnifiedScheduleTab } from './components/UnifiedScheduleTab';
import ModalConfirmApply from './ModalConfirmApply';
import { useQueryClient } from '@tanstack/react-query';
import { hasInvalidSchedule } from './utils/workGridMappers';

type Props = {
    setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
    date: string;
    onApplySuccess?: () => void;
};

type TabType = 'unified' | 'default' | 'exception';

// 요일 한글 -> dayOfWeek 매핑 (0=일, 1=월, ..., 6=토)
const DAY_TO_DOW: Record<string, number> = { 일: 0, 월: 1, 화: 2, 수: 3, 목: 4, 금: 5, 토: 6 };
const DOW_TO_DAY: Record<number, (typeof DAYS)[number]> = {
    0: '일',
    1: '월',
    2: '화',
    3: '수',
    4: '목',
    5: '금',
    6: '토',
};

const hhmmToIso = (hhmm: string | null): string => {
    if (!hhmm) return '';
    const [hourStr, minStr] = hhmm.split(':');
    return dayjs().hour(parseInt(hourStr, 10)).minute(parseInt(minStr, 10)).second(0).millisecond(0).toISOString();
};

const isoToHhmm = (iso: string | null): string | null => {
    if (!iso || iso === '') return null;
    return dayjs(iso).format('HH:mm');
};

const buildDefaultWeekSchedule = (): WeekSchedule => ({
    월: { enabled: false, timeSlots: [] },
    화: { enabled: false, timeSlots: [] },
    수: { enabled: false, timeSlots: [] },
    목: { enabled: false, timeSlots: [] },
    금: { enabled: false, timeSlots: [] },
    토: { enabled: false, timeSlots: [] },
    일: { enabled: false, timeSlots: [] },
});

const defaultWeekSchedule: WeekSchedule = buildDefaultWeekSchedule();

/** API 응답 -> UI WeekSchedule 변환 */
const apiRoutineToWeekSchedule = (weeklyRoutine: WeeklyRoutineItem[] | null): WeekSchedule => {
    if (!weeklyRoutine) return buildDefaultWeekSchedule();
    const schedule = buildDefaultWeekSchedule();
    weeklyRoutine.forEach((item) => {
        const day = DOW_TO_DAY[item.dayOfWeek];
        if (!day) return;
        schedule[day] = {
            enabled: item.isWorking,
            timeSlots: (item.timeSlots ?? []).map((slot) => ({
                startTime: hhmmToIso(slot.startTime),
                endTime: hhmmToIso(slot.endTime),
            })),
        };
    });
    return schedule;
};

/** UI WeekSchedule -> API 요청 형식 변환 */
const weekScheduleToApiRoutine = (schedule: WeekSchedule): WeeklyRoutineItem[] => {
    return DAYS.map((day) => {
        const s = schedule[day];
        const slots = s.enabled
            ? s.timeSlots
                  .map((slot) => {
                      const startHhmm = isoToHhmm(slot.startTime);
                      const endHhmm = isoToHhmm(slot.endTime);
                      if (!startHhmm || !endHhmm) return null;
                      return { startTime: startHhmm, endTime: endHhmm };
                  })
                  .filter((v): v is { startTime: string; endTime: string } => !!v)
            : [];
        return {
            dayOfWeek: DAY_TO_DOW[day],
            isWorking: s.enabled,
            timeSlots: slots,
        };
    });
};

/** API exceptions -> UI ExceptionInclude[] / ExceptionExclude[] */
const apiExceptionsToUI = (exceptions: WorkException[] | null) => {
    const includes: ExceptionInclude[] = [];
    const excludes: ExceptionExclude[] = [];
    if (!exceptions) return { includes, excludes };

    exceptions.forEach((ex, idx) => {
        if (ex.exceptionType === 'include') {
            includes.push({
                id: `inc-${idx}-${Date.now()}`,
                date: dayjs(ex.startTime).format('YYYY-MM-DD'),
                startTime: dayjs(ex.startTime).toISOString(),
                endTime: dayjs(ex.endTime).toISOString(),
                memo: ex.memo || '',
                isSaved: true,
                isMemoEditing: false,
            });
        } else {
            excludes.push({
                id: `exc-${idx}-${Date.now()}`,
                date: dayjs(ex.startTime).format('YYYY-MM-DD'),
                memo: ex.memo || '',
                isSaved: true,
                isMemoEditing: false,
            });
        }
    });
    return { includes, excludes };
};

/** UI -> API exceptions 변환 */
const uiExceptionsToApi = (includes: ExceptionInclude[], excludes: ExceptionExclude[]): WorkException[] => {
    const result: WorkException[] = [];

    includes.forEach((item) => {
        if (!item.date || !item.startTime || !item.endTime) return;
        const startHhmm = isoToHhmm(item.startTime);
        const endHhmm = isoToHhmm(item.endTime);
        if (!startHhmm || !endHhmm) return;
        result.push({
            exceptionType: 'include',
            startTime: dayjs(`${item.date}T${startHhmm}`).toISOString(),
            endTime: dayjs(`${item.date}T${endHhmm}`).toISOString(),
            memo: item.memo,
        });
    });

    excludes.forEach((item) => {
        if (!item.date) return;
        // exclude: 하루 전체 (KST 기준 00:00~다음날 00:00 -> UTC)
        result.push({
            exceptionType: 'exclude',
            startTime: dayjs(item.date).startOf('day').toISOString(),
            endTime: dayjs(item.date).endOf('day').add(1, 'ms').toISOString(),
            memo: item.memo,
        });
    });

    return result;
};

const ModalCoachWorkingHours = ({ setIsVisible, date, onApplySuccess }: Props) => {
    const authObject = getItemWithExpiration('authObject');
    const centerId = authObject?.centerId;
    const queryClient = useQueryClient();

    const [currentDate, setCurrentDate] = useState(dayjs(date));
    const [selectedTab, setSelectedTab] = useState<TabType>('default');
    const [selectedCoach, setSelectedCoach] = useState<CodeValueType<string> | undefined>(undefined);

    const year = String(currentDate.year());
    const month = String(currentDate.month() + 1);

    // API 조회
    const { data: routinesData, refetch: refetchRoutines } = useCoachWorkRoutines({
        centerId,
        year,
        month,
        enabled: !!centerId,
    });
    const { data: exceptionsData, refetch: refetchExceptions } = useCoachWorkExceptions({
        centerId,
        year,
        month,
        enabled: !!centerId,
    });

    // API 변경
    const patchRoutines = usePatchCoachWorkRoutines();
    const patchExceptions = usePatchCoachWorkExceptions();

    // 기본 근무 시간 상태 (강사별)
    const [weekScheduleMap, setWeekScheduleMap] = useState<Record<string, WeekSchedule>>({});
    const [initialWeekScheduleMap, setInitialWeekScheduleMap] = useState<Record<string, WeekSchedule>>({});

    // 예외 근무 날짜 상태 (강사별)
    const [exceptionIncludeMap, setExceptionIncludeMap] = useState<Record<string, ExceptionInclude[]>>({});
    const [initialExceptionIncludeMap, setInitialExceptionIncludeMap] = useState<Record<string, ExceptionInclude[]>>(
        {},
    );

    const [exceptionExcludeMap, setExceptionExcludeMap] = useState<Record<string, ExceptionExclude[]>>({});
    const [initialExceptionExcludeMap, setInitialExceptionExcludeMap] = useState<Record<string, ExceptionExclude[]>>(
        {},
    );

    // API 데이터 -> 로컬 상태 동기화
    useEffect(() => {
        if (routinesData?.coachWorkRoutines) {
            const newMap: Record<string, WeekSchedule> = {};
            routinesData.coachWorkRoutines.forEach((coach: CoachWorkRoutine) => {
                newMap[coach.coachId] = apiRoutineToWeekSchedule(coach.weeklyRoutine);
            });
            setWeekScheduleMap(newMap);
            setInitialWeekScheduleMap(newMap);
        }
    }, [routinesData]);

    useEffect(() => {
        if (exceptionsData?.coachWorkExceptions) {
            const newIncMap: Record<string, ExceptionInclude[]> = {};
            const newExcMap: Record<string, ExceptionExclude[]> = {};
            exceptionsData.coachWorkExceptions.forEach((coach) => {
                const { includes, excludes } = apiExceptionsToUI(coach.exceptions);
                newIncMap[coach.coachId] = includes;
                newExcMap[coach.coachId] = excludes;
            });
            setExceptionIncludeMap(newIncMap);
            setInitialExceptionIncludeMap(newIncMap);
            setExceptionExcludeMap(newExcMap);
            setInitialExceptionExcludeMap(newExcMap);
        }
    }, [exceptionsData]);

    // 월 변경 시 다시 조회
    useEffect(() => {
        refetchRoutines();
        refetchExceptions();
    }, [year, month]);

    const isCoachModified = useCallback(
        (id: string) => {
            const stripId = (items: any[]) => items.map(({ id, isMemoEditing, isSaved, ...rest }) => rest);
            const routineChanged = () => {
                const currentWeek = weekScheduleMap[id] ?? defaultWeekSchedule;
                const initialWeek = initialWeekScheduleMap[id] ?? defaultWeekSchedule;
                return JSON.stringify(currentWeek) !== JSON.stringify(initialWeek);
            };
            const exceptionChanged = () => {
                const currentInc = exceptionIncludeMap[id] ?? [];
                const initialInc = initialExceptionIncludeMap[id] ?? [];
                if (JSON.stringify(stripId(currentInc)) !== JSON.stringify(stripId(initialInc))) return true;
                const currentExc = exceptionExcludeMap[id] ?? [];
                const initialExc = initialExceptionExcludeMap[id] ?? [];
                if (JSON.stringify(stripId(currentExc)) !== JSON.stringify(stripId(initialExc))) return true;
                return false;
            };

            if (selectedTab === 'default') return routineChanged();
            if (selectedTab === 'exception') return exceptionChanged();
            // unified: 둘 중 하나라도 변경되었으면 modified
            return routineChanged() || exceptionChanged();
        },
        [
            selectedTab,
            weekScheduleMap,
            initialWeekScheduleMap,
            exceptionIncludeMap,
            initialExceptionIncludeMap,
            exceptionExcludeMap,
            initialExceptionExcludeMap,
        ],
    );

    const coachId = selectedCoach?.code ?? '';
    const weekSchedule = weekScheduleMap[coachId] ?? { ...defaultWeekSchedule };
    const exceptionIncludes = exceptionIncludeMap[coachId] ?? [];
    const exceptionExcludes = exceptionExcludeMap[coachId] ?? [];

    const includeEnabled = exceptionIncludes.length > 0;
    const excludeEnabled = exceptionExcludes.length > 0;

    const setWeekSchedule = (schedule: WeekSchedule) => {
        setWeekScheduleMap((prev) => ({ ...prev, [coachId]: schedule }));
    };

    const setExceptionIncludes = (items: ExceptionInclude[]) => {
        setExceptionIncludeMap((prev) => ({ ...prev, [coachId]: items }));
    };

    const setExceptionExcludes = (items: ExceptionExclude[]) => {
        setExceptionExcludeMap((prev) => ({ ...prev, [coachId]: items }));
    };

    const filteredCoachList: CodeValueType<string>[] = useMemo(() => {
        if (!routinesData?.coachWorkRoutines) return [];
        return routinesData.coachWorkRoutines.map((coach: CoachWorkRoutine) => ({
            code: coach.coachId,
            value: coach.coachName,
        }));
    }, [routinesData]);

    // Update selected coach when the list is populated for the first time
    useEffect(() => {
        if (filteredCoachList.length > 0 && !selectedCoach) {
            setSelectedCoach(filteredCoachList[0]);
        }
    }, [filteredCoachList, selectedCoach]);

    const handlePrevMonth = () => setCurrentDate(currentDate.subtract(1, 'month'));
    const handleNextMonth = () => setCurrentDate(currentDate.add(1, 'month'));

    const handleDayToggle = (day: (typeof DAYS)[number]) => {
        const cur = weekSchedule[day];
        const willEnable = !cur.enabled;
        setWeekSchedule({
            ...weekSchedule,
            [day]: {
                enabled: willEnable,
                // 활성화하는데 슬롯이 비어있으면 빈 슬롯 1개 추가
                timeSlots: willEnable
                    ? cur.timeSlots.length > 0
                        ? cur.timeSlots
                        : [{ startTime: '', endTime: '' }]
                    : [],
            },
        });
    };

    const handleSlotChange = (
        day: (typeof DAYS)[number],
        slotIndex: number,
        field: 'startTime' | 'endTime',
        value: string,
    ) => {
        const cur = weekSchedule[day];
        const next = cur.timeSlots.map((slot, i) =>
            i === slotIndex ? { ...slot, [field]: value } : slot,
        );
        setWeekSchedule({ ...weekSchedule, [day]: { ...cur, timeSlots: next } });
    };

    const handleAddSlot = (day: (typeof DAYS)[number]) => {
        const cur = weekSchedule[day];
        setWeekSchedule({
            ...weekSchedule,
            [day]: {
                enabled: true,
                timeSlots: [...cur.timeSlots, { startTime: '', endTime: '' }],
            },
        });
    };

    const handleRemoveSlot = (day: (typeof DAYS)[number], slotIndex: number) => {
        const cur = weekSchedule[day];
        const remaining = cur.timeSlots.filter((_, i) => i !== slotIndex);
        setWeekSchedule({
            ...weekSchedule,
            [day]: {
                enabled: remaining.length > 0,
                timeSlots: remaining,
            },
        });
    };

    /** 일괄 적용: 모든 enabled 요일의 슬롯을 [{start, end}] 단일 슬롯으로 덮어쓰기 (deprecated, 통합 탭 등 외부에서만 사용 가능) */
    const handleBatchTimeApply = (startTime: string, endTime: string) => {
        const updated = { ...weekSchedule };
        DAYS.forEach((day) => {
            if (updated[day].enabled) {
                updated[day] = {
                    enabled: true,
                    timeSlots: [{ startTime, endTime }],
                };
            }
        });
        setWeekSchedule(updated);
    };

    /** 현재 강사의 근무 시간을 모든 강사에게 복사 */
    const handleApplyToAllCoaches = () => {
        if (!coachId || !weekSchedule) return;
        // 깊은 복사로 모든 강사에게 동일 스케줄 주입
        const cloned = JSON.parse(JSON.stringify(weekSchedule)) as WeekSchedule;
        const newMap: Record<string, WeekSchedule> = {};
        filteredCoachList.forEach((coach) => {
            newMap[coach.code] = JSON.parse(JSON.stringify(cloned));
        });
        setWeekScheduleMap(newMap);
    };

    const handleAddExceptionInclude = () => {
        setExceptionIncludes([
            ...exceptionIncludes,
            {
                id: Date.now().toString(),
                date: dayjs().format('YYYY-MM-DD'),
                startTime: '',
                endTime: '',
                memo: '',
                isSaved: false,
                isMemoEditing: false,
            },
        ]);
    };

    const handleRemoveExceptionInclude = (id: string) => {
        setExceptionIncludes(exceptionIncludes.filter((item) => item.id !== id));
    };

    const handleAddExceptionExclude = () => {
        setExceptionExcludes([
            ...exceptionExcludes,
            {
                id: Date.now().toString(),
                date: dayjs().format('YYYY-MM-DD'),
                memo: '',
                isSaved: false,
                isMemoEditing: false,
            },
        ]);
    };

    const handleRemoveExceptionExclude = (id: string) => {
        setExceptionExcludes(exceptionExcludes.filter((item) => item.id !== id));
    };

    const handleApply = async () => {
        if (!centerId) return;

        const saveRoutines = async () => {
            const coachWorkRoutines: CoachWorkRoutineBody[] = filteredCoachList.map((coach) => ({
                coachId: coach.code,
                weeklyRoutine: weekScheduleToApiRoutine(weekScheduleMap[coach.code] ?? defaultWeekSchedule),
            }));
            await patchRoutines.mutateAsync({
                centerId,
                body: { year, month, coachWorkRoutines },
            });
        };

        const saveExceptions = async () => {
            const workExceptionArray: CoachWorkExceptionBody[] = filteredCoachList.map((coach) => {
                const inc = exceptionIncludeMap[coach.code] ?? [];
                const exc = exceptionExcludeMap[coach.code] ?? [];
                const exceptions = uiExceptionsToApi(inc, exc);
                return { coachId: coach.code, exceptions };
            });
            await patchExceptions.mutateAsync({
                centerId,
                body: { year, month, workExceptionArray },
            });
        };

        try {
            if (selectedTab === 'default') {
                await saveRoutines();
            } else if (selectedTab === 'exception') {
                await saveExceptions();
            } else {
                // unified: 두 API 모두 저장
                await saveRoutines();
                await saveExceptions();
            }

            setIsVisible(false);
            if (onApplySuccess) {
                onApplySuccess();
            }
        } catch (error) {
            console.error('근무시간 저장 실패:', error);
        }
    };

    const isApplyDisabled = useMemo(() => {
        const routinesInvalid = Object.values(weekScheduleMap).some((schedule) =>
            hasInvalidSchedule(schedule),
        );
        const exceptionsInvalid =
            Object.values(exceptionIncludeMap).some((includes) => includes.some((inc) => !inc.isSaved)) ||
            Object.values(exceptionExcludeMap).some((excludes) => excludes.some((exc) => !exc.isSaved));

        if (selectedTab === 'default') return routinesInvalid;
        if (selectedTab === 'exception') return exceptionsInvalid;
        // unified: 둘 다 유효해야 저장 가능
        return routinesInvalid || exceptionsInvalid;
    }, [selectedTab, exceptionIncludeMap, exceptionExcludeMap, weekScheduleMap]);

    const isSubmitting = patchRoutines.isLoading || patchExceptions.isLoading;

    return (
        <Modal setIsVisible={setIsVisible} isCloseOutside={false} borderRadius={37} modalAlign="center">
            <Col
                className={`${
                    selectedTab === 'unified' ? 'w-[min(96vw,1680px)]' : 'w-[895px]'
                } min-h-[928px] px-[38px] py-[36px]`}
            >
                {/* 헤더 - 날짜 네비게이션 */}
                <Row className="items-center mb-[50px]">
                    <div className="cursor-pointer mr-[20px]" onClick={handlePrevMonth}>
                        <SvgIcon
                            name="SvgLeftStrokeArrow"
                            size={18}
                            fill={colors.trans}
                            stroke="#808B96"
                            strokeWidth={2}
                        />
                    </div>
                    <MediumText fontSize={22} color="#181D24" letterSpacing={-0.5} className="w-[120px] text-center">
                        {currentDate.format('YYYY년 M월')}
                    </MediumText>
                    <div className="cursor-pointer ml-[20px]" onClick={handleNextMonth}>
                        <SvgIcon
                            name="SvgRightStrokeArrow"
                            size={18}
                            fill={colors.trans}
                            stroke="#808B96"
                            strokeWidth={2}
                        />
                    </div>
                    <MediumText fontSize={22} color="#3D4244" letterSpacing={-0.5} className="ml-[29px]">
                        강사별 근무 시간 설정
                    </MediumText>
                </Row>

                {/* 탭 */}
                <div className="flex items-end mb-[32px]">
                    <div className="relative">
                        <Row>
                            {(
                                [
                                    { id: 'default', label: '기본 근무 시간' },
                                    { id: 'exception', label: '예외 근무 날짜' },
                                    { id: 'unified', label: '통합 근무 입력', isNew: true },
                                ] as { id: TabType; label: string; isNew?: boolean }[]
                            ).map((tab) => (
                                <React.Fragment key={tab.id}>
                                    <div
                                        className="cursor-pointer pb-[12px] flex flex-nowrap items-center justify-center w-[200px] gap-[6px] whitespace-nowrap"
                                        onClick={() => setSelectedTab(tab.id)}
                                    >
                                        <MediumText
                                            fontSize={20}
                                            lineHeight={24}
                                            color={selectedTab === tab.id ? '#1A1A1A' : '#6B7280'}
                                        >
                                            {tab.label}
                                        </MediumText>
                                        {tab.isNew && (
                                            <span className="inline-flex items-center justify-center px-[5px] min-h-[20px] bg-[#FF5A36] text-white text-[13px] leading-none rounded-[4px] font-semibold tracking-wide">
                                                NEW
                                            </span>
                                        )}
                                    </div>
                                    <div className="w-[16px]" />
                                </React.Fragment>
                            ))}
                        </Row>

                        {/* 밑줄 (회색 배경선) */}
                        <div className="absolute bottom-0 left-0 w-[648px] h-[1px] bg-[#E0E0E0]" />

                        {/* 애니메이션 검은선 */}
                        <div
                            className="absolute bottom-0 h-[2.5px] bg-[#1A1A1A] transition-transform duration-300 ease-in-out"
                            style={{
                                width: '200px',
                                transform:
                                    selectedTab === 'default'
                                        ? 'translateX(0)'
                                        : selectedTab === 'exception'
                                          ? 'translateX(216px)'
                                          : 'translateX(432px)',
                            }}
                        />
                    </div>

                </div>

                {/* 메인 컨텐츠 */}
                {selectedTab === 'unified' ? (
                    <Col className="flex-1">
                        <UnifiedScheduleTab
                            centerId={centerId}
                            coaches={filteredCoachList.map((c) => ({ coachId: c.code, coachName: c.value }))}
                            year={parseInt(year, 10)}
                            month={parseInt(month, 10)}
                            weekScheduleMap={weekScheduleMap}
                            exceptionIncludeMap={exceptionIncludeMap}
                            exceptionExcludeMap={exceptionExcludeMap}
                            onWeekScheduleChange={(coachId, next) =>
                                setWeekScheduleMap((prev) => ({ ...prev, [coachId]: next }))
                            }
                            onIncludeMapChange={setExceptionIncludeMap}
                            onExcludeMapChange={setExceptionExcludeMap}
                            onWeekScheduleMapMerge={(partial) =>
                                setWeekScheduleMap((prev) => ({ ...prev, ...partial }))
                            }
                            isCoachModified={isCoachModified}
                        />
                    </Col>
                ) : (
                    <Row className="flex-1">
                        {/* 왼쪽 강사 리스트 */}
                        <Col className="w-[164px] min-w-[164px] mr-[20px] gap-[10px]">
                            <DemiLightText fontSize={14} lineHeight={20} color="#6B7280" className="mb-[21px]">
                                강사 리스트
                            </DemiLightText>
                            {filteredCoachList.map((coach) => {
                                const modified = isCoachModified(coach.code);
                                return (
                                    <div
                                        key={coach.code}
                                        className={`flex flex-row items-center px-[16px] py-[10px] cursor-pointer mb-[2px] rounded-[12px] transition-colors justify-between ${
                                            selectedCoach?.code === coach.code ? 'bg-[#F2F4F6]' : 'hover:bg-[#F2F4F6]'
                                        }`}
                                        onClick={() => setSelectedCoach(coach)}
                                    >
                                        {selectedCoach?.code === coach.code ? (
                                            <MediumText fontSize={18} lineHeight={24} color="#3D73DD">
                                                {coach.value}
                                            </MediumText>
                                        ) : (
                                            <RegularText fontSize={18} lineHeight={24} color="#3D4244">
                                                {coach.value}
                                            </RegularText>
                                        )}
                                        {modified && (
                                            <div className="w-[6px] h-[6px] bg-[#3D73DD] rounded-full ml-[4px]" />
                                        )}
                                    </div>
                                );
                            })}
                        </Col>

                        {/* 구분선 */}
                        <div className="w-[0.5px] bg-[#E6E8EA] mr-[20px]" />

                        {/* 오른쪽 컨텐츠 */}
                        <Col className="flex-1">
                            {selectedTab === 'default' ? (
                                <DefaultScheduleTab
                                    coachName={selectedCoach?.value ?? ''}
                                    weekSchedule={weekSchedule}
                                    onDayToggle={handleDayToggle}
                                    onSlotChange={handleSlotChange}
                                    onAddSlot={handleAddSlot}
                                    onRemoveSlot={handleRemoveSlot}
                                    onApplyToAll={handleApplyToAllCoaches}
                                />
                            ) : (
                                <ExceptionScheduleTab
                                    exceptionIncludes={exceptionIncludes}
                                    exceptionExcludes={exceptionExcludes}
                                    includeEnabled={includeEnabled}
                                    excludeEnabled={excludeEnabled}
                                    onIncludeToggle={() => {
                                        if (!includeEnabled) {
                                            handleAddExceptionInclude();
                                        } else {
                                            setExceptionIncludes([]);
                                        }
                                    }}
                                    onExcludeToggle={() => {
                                        if (!excludeEnabled) {
                                            handleAddExceptionExclude();
                                        } else {
                                            setExceptionExcludes([]);
                                        }
                                    }}
                                    onAddInclude={handleAddExceptionInclude}
                                    onRemoveInclude={handleRemoveExceptionInclude}
                                    onUpdateInclude={(id, field, value) => {
                                        setExceptionIncludes(
                                            exceptionIncludes.map((item) =>
                                                item.id === id ? { ...item, [field]: value } : item,
                                            ),
                                        );
                                    }}
                                    onAddExclude={handleAddExceptionExclude}
                                    onRemoveExclude={handleRemoveExceptionExclude}
                                    onUpdateExclude={(id, field, value) => {
                                        setExceptionExcludes(
                                            exceptionExcludes.map((item) =>
                                                item.id === id ? { ...item, [field]: value } : item,
                                            ),
                                        );
                                    }}
                                />
                            )}
                        </Col>
                    </Row>
                )}

                {/* 하단 버튼 */}
                <Row className="flex flex-row justify-between items-center gap-[8px]">
                    {selectedTab === 'exception' ? (
                        <Row className="items-center gap-[8px]">
                            <SvgIcon name="SvgWarningInCircle" size={17} fill={'#848F9A'} />
                            <RegularText fontSize={14} lineHeight={20} color="#6B7280">
                                기본 근무 시간보다 우선 적용돼요
                            </RegularText>
                        </Row>
                    ) : (
                        <div />
                    )}
                    <Row className="gap-[8px]">
                        <div
                            className="w-[240px] h-[56px] bg-[#F4F5F7] rounded-[12px] flex items-center justify-center cursor-pointer"
                            onClick={() => setIsVisible(false)}
                        >
                            <RegularText fontSize={20} lineHeight={24} color="#3D4244">
                                취소
                            </RegularText>
                        </div>
                        <div
                            className={`w-[240px] h-[56px] rounded-[12px] flex items-center justify-center ${
                                isSubmitting || isApplyDisabled
                                    ? 'bg-[#3D73DD] opacity-50 cursor-not-allowed'
                                    : 'bg-[#3D73DD] cursor-pointer'
                            }`}
                            onClick={
                                isSubmitting || isApplyDisabled
                                    ? undefined
                                    : () => {
                                          overlay.open(({ unmount }) => (
                                              <ModalConfirmApply onClose={unmount} onConfirm={handleApply} />
                                          ));
                                      }
                            }
                        >
                            <RegularText fontSize={20} lineHeight={24} color="#FFFFFF">
                                {isSubmitting ? '저장 중...' : '적용하기'}
                            </RegularText>
                        </div>
                    </Row>
                </Row>
            </Col>
        </Modal>
    );
};

export default ModalCoachWorkingHours;
